/**
 * /workflows/tenants/:id/reassign-product — three-step stepper:
 *  1) Clear current brand (PUT /tenants/{id}/brand { brandId: null })
 *  2) Change product   (PUT /tenants/{id}/product { productId })
 *  3) Pick new brand   (PUT /tenants/{id}/brand { brandId })
 *
 * SASADM-0171 (SASATH-0389 §4.1) — a `409 APP_HAS_TENANT_OVERRIDES` shows the sentence plus links
 * to the listed tenants instead of the generic banner.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { BrandDto, ProductDto, TenantDto } from '@/api/types';
import { getBrandsByProduct, getProducts, invalidateBrandsForProduct } from '@/api/lookupCache';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { TenantOverridesNotice } from '@/components/TenantOverridesNotice';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

export function ReassignProductWizard() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const { t } = useCopy();
  const navigate = useNavigate();
  const snack = useSnackbar();
  const [tenant, setTenant] = useState<TenantDto | null>(null);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [newProductId, setNewProductId] = useState<number | ''>('');
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [newBrandId, setNewBrandId] = useState<number | null>(null);

  useEffect(() => {
    void apiAdmin.tenants.get(id).then((tn) => {
      setTenant(tn);
      if (!tn.brandId) setActive(1);
    });
    void getProducts().then(setProducts);
  }, [id]);

  useEffect(() => {
    if (typeof newProductId === 'number') {
      void getBrandsByProduct(newProductId).then(setBrands);
    }
  }, [newProductId]);

  async function clearBrand() {
    setBusy(true);
    setError(null);
    try {
      await apiAdmin.tenants.setBrand(id, null);
      const tn = await apiAdmin.tenants.get(id);
      setTenant(tn);
      setActive(1);
      snack.success(t('snackbar.brandCleared', 'Brand cleared.'));
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function changeProduct() {
    if (typeof newProductId !== 'number') return;
    setBusy(true);
    setError(null);
    try {
      await apiAdmin.tenants.setProduct(id, newProductId);
      const tn = await apiAdmin.tenants.get(id);
      setTenant(tn);
      setActive(2);
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function pickBrand() {
    setBusy(true);
    setError(null);
    try {
      await apiAdmin.tenants.setBrand(id, newBrandId);
      if (typeof newProductId === 'number') invalidateBrandsForProduct(newProductId);
      snack.success(t('snackbar.brandAssigned', 'Brand assigned.'));
      navigate(`/tenants/${id}`);
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!tenant) return null;

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.tenants', 'Tenants'), to: '/tenants' },
          { label: tenant.name, to: `/tenants/${id}` },
          { label: t('wizards.reassignProduct.title', 'Reassign product') },
        ]}
      />
      <Typography variant="h1" gutterBottom>
        {t('wizards.reassignProduct.title', 'Reassign product')} — {tenant.name}
      </Typography>
      <Stepper activeStep={active} sx={{ mb: 3 }} alternativeLabel>
        <Step>
          <StepLabel>{t('wizards.reassignProduct.step1', '')}</StepLabel>
        </Step>
        <Step>
          <StepLabel>{t('wizards.reassignProduct.step2', '')}</StepLabel>
        </Step>
        <Step>
          <StepLabel>{t('wizards.reassignProduct.step3', '')}</StepLabel>
        </Step>
      </Stepper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        {error?.code === 'APP_HAS_TENANT_OVERRIDES' ? (
          <TenantOverridesNotice err={error} />
        ) : (
          <ErrorBanner error={error} />
        )}
        {active === 0 && (
          <Stack spacing={2}>
            <Alert severity="warning">{t('wizards.reassignProduct.preempt', '')}</Alert>
            <Box>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => void clearBrand()}
                startIcon={busy ? <CircularProgress size={16} /> : null}
              >
                {t('branding.clear', 'Clear brand')}
              </Button>
            </Box>
          </Stack>
        )}
        {active === 1 && (
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label="New product"
              value={newProductId === '' ? '' : String(newProductId)}
              onChange={(e) => setNewProductId(Number(e.target.value))}
            >
              {products
                .filter((p) => p.id !== tenant.productId)
                .map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.displayName} ({p.slug})
                  </MenuItem>
                ))}
            </TextField>
            <Box>
              <Button
                variant="contained"
                disabled={busy || typeof newProductId !== 'number'}
                onClick={() => void changeProduct()}
                startIcon={busy ? <CircularProgress size={16} /> : null}
              >
                {t('common.next', 'Next')}
              </Button>
            </Box>
          </Stack>
        )}
        {active === 2 && (
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label="New brand"
              value={newBrandId ?? 'inherit'}
              onChange={(e) => setNewBrandId(e.target.value === 'inherit' ? null : Number(e.target.value))}
            >
              <MenuItem value="inherit">{t('branding.inherits', 'Inherit product default')}</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.displayName} ({b.slug})
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => void pickBrand()}
                startIcon={busy ? <CircularProgress size={16} /> : null}
              >
                {t('common.save', 'Save')}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
